import { useEffect, useState } from "react";
import { api, formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Mail, Phone, Calendar as CalendarIcon, Users, Euro, Home, X, Trash2, Save } from "lucide-react";

const field =
  "w-full bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-2 text-[#0A1128] text-sm";

const diffNights = (ci, co) => {
  if (!ci || !co) return 0;
  const a = new Date(ci);
  const b = new Date(co);
  return Math.max(0, Math.round((b - a) / (1000 * 60 * 60 * 24)));
};

const BookingDetailModal = ({ booking, houses = [], open, onClose, onChanged }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (booking) {
      setForm({
        guest_name: booking.guest_name || "",
        guest_email: booking.guest_email || "",
        guest_phone: booking.guest_phone || "",
        check_in: booking.check_in || "",
        check_out: booking.check_out || "",
        guests_count: booking.guests_count || 1,
        amount: booking.amount || 0,
        house_id: booking.house_id || "",
        notes: booking.notes || "",
      });
    }
  }, [booking]);

  if (!booking || !form) return null;

  const house = houses.find((h) => h.id === booking.house_id);
  const source = booking.source === "stripe" ? "Stripe" : "Manual";

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const nights = diffNights(form.check_in, form.check_out);
      const payload = {
        ...form,
        nights,
        guests_count: Number(form.guests_count),
        amount: Number(form.amount),
      };
      await api.put(`/bookings/${booking.id}`, payload);
      toast.success("Reserva atualizada");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Tem a certeza que quer apagar esta reserva?")) return;
    setDeleting(true);
    try {
      await api.delete(`/bookings/${booking.id}`);
      toast.success("Reserva apagada");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-2xl bg-[#FCFAF8] border-[#0A1128]/15 rounded-none p-0"
        data-testid="booking-detail-modal"
      >
        {/* Header */}
        <div className="bg-[#0A1128] text-[#F4F1EB] px-8 py-6">
          <DialogHeader>
            <div className="overline text-[#C5A059] mb-1">Reserva · {source}</div>
            <DialogTitle className="font-serif-display text-3xl font-light text-[#F4F1EB]">
              {booking.guest_name}
            </DialogTitle>
            <div className="text-xs text-[#F4F1EB]/70 mt-2 flex items-center gap-4 flex-wrap">
              {house && (
                <span className="flex items-center gap-1.5">
                  <Home size={12} /> {house.name}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <CalendarIcon size={12} /> {booking.check_in} → {booking.check_out}
              </span>
              <span className="flex items-center gap-1.5">
                <Euro size={12} /> {Math.round(booking.amount || 0)}
              </span>
            </div>
          </DialogHeader>
        </div>

        <form onSubmit={handleSave} className="px-8 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Contact block */}
          <div>
            <div className="overline mb-3">Contactos do hóspede</div>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                className={field}
                placeholder="Nome do hóspede"
                value={form.guest_name}
                onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                data-testid="edit-booking-guest-name"
              />
              <input
                className={field}
                type="email"
                placeholder="Email"
                value={form.guest_email}
                onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                data-testid="edit-booking-email"
              />
              <input
                className={field}
                placeholder="Telefone"
                value={form.guest_phone}
                onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                data-testid="edit-booking-phone"
              />
              <select
                className={field}
                value={form.house_id}
                onChange={(e) => setForm({ ...form, house_id: e.target.value })}
                data-testid="edit-booking-house"
              >
                {houses.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates / amount */}
          <div>
            <div className="overline mb-3">Estadia</div>
            <div className="grid md:grid-cols-4 gap-4">
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E] mb-1">Check-in</span>
                <input
                  type="date"
                  className={field}
                  value={form.check_in}
                  onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                  data-testid="edit-booking-checkin"
                />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E] mb-1">Check-out</span>
                <input
                  type="date"
                  className={field}
                  value={form.check_out}
                  onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                  data-testid="edit-booking-checkout"
                />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E] mb-1">Hóspedes</span>
                <input
                  type="number"
                  min={1}
                  className={field}
                  value={form.guests_count}
                  onChange={(e) => setForm({ ...form, guests_count: e.target.value })}
                  data-testid="edit-booking-guests"
                />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E] mb-1">Valor (€)</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className={field}
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="edit-booking-amount"
                />
              </label>
            </div>
            <div className="text-xs text-[#3A435E] mt-3">
              {diffNights(form.check_in, form.check_out)} noite(s)
            </div>
          </div>

          {/* Notes */}
          <div>
            <div className="overline mb-3">Notas internas</div>
            <textarea
              className={field}
              rows={2}
              placeholder="Notas sobre a reserva (opcional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="edit-booking-notes"
            />
          </div>

          {/* Metadata */}
          <div className="text-xs text-[#7B86A4] space-y-1 border-t border-[#0A1128]/10 pt-4">
            {booking.guest_email && (
              <div className="flex items-center gap-2">
                <Mail size={12} /> <a href={`mailto:${booking.guest_email}`} className="hover:text-[#C5A059]">{booking.guest_email}</a>
              </div>
            )}
            {booking.guest_phone && (
              <div className="flex items-center gap-2">
                <Phone size={12} /> <a href={`tel:${booking.guest_phone}`} className="hover:text-[#C5A059]">{booking.guest_phone}</a>
              </div>
            )}
            {booking.payment_session_id && (
              <div>Session: <span className="font-mono">{booking.payment_session_id.slice(0, 24)}…</span></div>
            )}
            {booking.created_at && (
              <div>Criada em {new Date(booking.created_at).toLocaleString("pt-PT")}</div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-[#0A1128]/10">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="text-[#9B2226] text-xs tracking-[0.2em] uppercase flex items-center gap-2 hover:underline disabled:opacity-50"
              data-testid="delete-booking-from-modal"
            >
              <Trash2 size={14} /> {deleting ? "A apagar…" : "Apagar reserva"}
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="border border-[#0A1128]/20 px-5 py-2.5 text-xs tracking-[0.2em] uppercase hover:bg-[#0A1128]/5"
                data-testid="cancel-edit-booking"
              >
                <X size={12} className="inline mr-1" /> Fechar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-[#0A1128] text-white hover:bg-[#162243] px-6 py-2.5 text-xs tracking-[0.2em] uppercase flex items-center gap-2 disabled:opacity-50"
                data-testid="save-edit-booking"
              >
                <Save size={12} /> {saving ? "A guardar…" : "Guardar"}
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDetailModal;
